/**
 * CalendarDayView Component
 * Displays appointments for a single day with improved time blocks
 */

interface CalendarDayViewProps {
  selectedDate: Date;
  appointments: any[];
  onAppointmentClick: (appointment: any) => void;
  onTimeSlotClick: (date: Date, hour: number) => void;
}

export function CalendarDayView({
  selectedDate,
  appointments,
  onAppointmentClick,
  onTimeSlotClick,
}: CalendarDayViewProps) {
  const getAppointmentsForDay = () => {
    return appointments
      .filter((apt) => {
        const aptDate = new Date(apt.startTime);
        return (
          aptDate.getDate() === selectedDate.getDate() &&
          aptDate.getMonth() === selectedDate.getMonth() &&
          aptDate.getFullYear() === selectedDate.getFullYear()
        );
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  };

  // Dynamically determine time range based on appointments
  // Default to business hours (8 AM - 6 PM), but expand if appointments exist outside this range
  const getTimeSlots = () => {
    let startHour = 8;
    let endHour = 18;

    // Check if any appointments fall outside business hours
    if (dayAppointments.length > 0) {
      dayAppointments.forEach((apt) => {
        const aptStartHour = new Date(apt.startTime).getHours();
        const aptEndHour = new Date(apt.endTime).getHours();
        
        if (aptStartHour < startHour) {
          startHour = aptStartHour;
        }
        if (aptEndHour > endHour) {
          endHour = aptEndHour;
        }
      });
    }

    const slots: { hour: number; label: string; isBusinessHours: boolean }[] = [];
    for (let hour = startHour; hour <= endHour; hour++) {
      const time = new Date();
      time.setHours(hour, 0, 0, 0);
      slots.push({
        hour,
        label: time.toLocaleTimeString('en-US', {
          hour: 'numeric',
          hour12: true,
        }),
        isBusinessHours: hour >= 8 && hour <= 18,
      });
    }
    return slots;
  };

  const dayAppointments = getAppointmentsForDay();
  const timeSlots = getTimeSlots();

  const isToday = () => {
    const today = new Date();
    return (
      selectedDate.getDate() === today.getDate() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getFullYear() === today.getFullYear()
    );
  };

  return (
    <div className="p-2 sm:p-4">
      {/* Day header - more compact */}
      <div className="mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-gray-300">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-xl font-bold text-gray-900 truncate">
              {selectedDate.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </h3>
            {isToday() && (
              <span className="inline-block mt-1 px-2 py-0.5 text-[10px] sm:text-xs text-white bg-[#C41E3A] rounded-full font-medium">
                Today
              </span>
            )}
          </div>
          <div className="text-right ml-2 flex-shrink-0">
            <div className="text-xl sm:text-2xl font-bold text-[#C41E3A]">
              {dayAppointments.length}
            </div>
            <div className="text-[10px] sm:text-xs text-gray-600">
              {dayAppointments.length === 1 ? 'Appt' : 'Appts'}
            </div>
          </div>
        </div>
      </div>

      {/* Time grid - more compact */}
      <div className="space-y-0">
        {timeSlots.map(({ hour, label, isBusinessHours }) => {
          const hourAppointments = dayAppointments.filter((apt) => {
            const aptHour = new Date(apt.startTime).getHours();
            return aptHour === hour;
          });

          return (
            <div key={hour} className={`flex border-b border-gray-200 hover:bg-gray-50 transition-colors group ${
              !isBusinessHours ? 'bg-amber-50/20' : ''
            }`}>
              {/* Time label - clickable and more compact */}
              <button
                onClick={() => onTimeSlotClick(selectedDate, hour)}
                className={`w-16 sm:w-24 flex-shrink-0 py-1.5 sm:py-2 pr-2 sm:pr-4 text-right border-r border-gray-300 hover:bg-red-50 transition-colors cursor-pointer ${
                  !isBusinessHours ? 'bg-amber-50' : ''
                }`}
                title="Create appointment at this time"
              >
                <span className="text-xs sm:text-sm text-gray-700 font-semibold group-hover:text-[#C41E3A]">{label}</span>
                {!isBusinessHours && (
                  <span className="block text-[8px] sm:text-[10px] text-amber-600">After</span>
                )}
                <svg 
                  className="w-2.5 h-2.5 sm:w-3 sm:h-3 inline-block ml-0.5 sm:ml-1 text-[#C41E3A] opacity-0 group-hover:opacity-100 transition-opacity" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>

              {/* Appointment slot - more compact, handles multiple appointments */}
              <div className="flex-1 min-h-[50px] sm:min-h-[60px] py-1.5 sm:py-2 pl-2 sm:pl-4 relative">
                {hourAppointments.length === 0 ? (
                  <div className="h-full flex items-center">
                    {/* Empty slot */}
                  </div>
                ) : hourAppointments.length === 1 ? (
                  // Single appointment - full width
                  <div className="space-y-2">
                    {hourAppointments.map((apt) => {
                      const startTime = new Date(apt.startTime);
                      const endTime = new Date(apt.endTime);
                      const duration =
                        (endTime.getTime() - startTime.getTime()) / (1000 * 60);

                      return (
                        <div
                          key={apt.id}
                          className="bg-red-50 border-l-4 border-[#C41E3A] rounded-r-lg p-2 sm:p-3 cursor-pointer hover:bg-red-100 hover:shadow-md transition-all"
                          onClick={() => onAppointmentClick(apt)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-bold text-gray-900 text-sm sm:text-base truncate">
                                {apt.title}
                              </h4>
                              <div className="flex items-center gap-1.5 sm:gap-2 mt-1 text-[10px] sm:text-xs text-gray-700 flex-wrap">
                                <div className="flex items-center gap-0.5 sm:gap-1">
                                  <svg
                                    className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#C41E3A] flex-shrink-0"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                  <span className="font-medium">
                                    {startTime.toLocaleTimeString('en-US', {
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true,
                                    })}{' '}
                                    -{' '}
                                    {endTime.toLocaleTimeString('en-US', {
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true,
                                    })}
                                  </span>
                                </div>
                                <span className="text-gray-400 hidden sm:inline">•</span>
                                <span className="font-medium">{duration} min</span>
                              </div>
                              {apt.location && (
                                <div className="flex items-center gap-0.5 sm:gap-1 mt-1 text-[10px] sm:text-xs text-gray-700">
                                  <svg
                                    className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#C41E3A] flex-shrink-0"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                  </svg>
                                  <span className="font-medium truncate">{apt.location}</span>
                                </div>
                              )}
                              {apt.description && (
                                <p className="mt-2 text-[10px] sm:text-xs text-gray-700 bg-white p-1.5 sm:p-2 rounded border border-gray-200 line-clamp-2">
                                  {apt.description}
                                </p>
                              )}
                              {apt.attendees && apt.attendees.length > 0 && (
                                <div className="flex items-center gap-0.5 sm:gap-1 mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-700">
                                  <svg
                                    className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#C41E3A] flex-shrink-0"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                    />
                                  </svg>
                                  <span className="font-medium truncate">
                                    {apt.attendees.map((attendee: any) => `${attendee.firstName} ${attendee.lastName}`).join(', ')}
                                  </span>
                                </div>
                              )}
                            </div>
                            {apt.googleCalendarEventId && (
                              <div className="ml-2 sm:ml-3 flex-shrink-0">
                                <div className="flex items-center gap-0.5 sm:gap-1 text-[9px] sm:text-[10px] text-green-700 bg-green-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-medium">
                                  <svg
                                    className="w-2.5 h-2.5 sm:w-3 sm:h-3"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  <span className="hidden sm:inline">Synced</span>
                                  <span className="sm:hidden">✓</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  // Multiple appointments - compact grid layout
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2">
                    {hourAppointments.map((apt) => {
                      const startTime = new Date(apt.startTime);
                      const endTime = new Date(apt.endTime);
                      const duration =
                        (endTime.getTime() - startTime.getTime()) / (1000 * 60);

                      return (
                        <div
                          key={apt.id}
                          className="bg-[#C41E3A] text-white rounded-lg p-1.5 sm:p-2 cursor-pointer hover:bg-[#8B0000] transition-all shadow-md"
                          onClick={() => onAppointmentClick(apt)}
                        >
                          <h4 className="font-bold text-xs sm:text-sm truncate">
                            {apt.title}
                          </h4>
                          <div className="text-[9px] sm:text-[10px] opacity-90 mt-0.5 sm:mt-1">
                            {startTime.toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true,
                            })}{' '}
                            - {duration}m
                          </div>
                          {apt.location && (
                            <div className="text-[9px] sm:text-[10px] opacity-75 truncate mt-0.5 sm:mt-1">
                              📍 {apt.location}
                            </div>
                          )}
                          {apt.attendees && apt.attendees.length > 0 && (
                            <div className="text-[9px] sm:text-[10px] opacity-75 truncate mt-0.5 sm:mt-1">
                              👥 {apt.attendees.map((attendee: any) => `${attendee.firstName} ${attendee.lastName}`).join(', ')}
                            </div>
                          )}
                          {apt.googleCalendarEventId && (
                            <div className="flex items-center gap-0.5 sm:gap-1 text-[8px] sm:text-[9px] bg-green-500 px-1 sm:px-1.5 py-0.5 rounded-full font-medium mt-0.5 sm:mt-1 w-fit">
                              <svg
                                className="w-2 h-2 sm:w-2.5 sm:h-2.5"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span className="hidden sm:inline">Synced</span>
                              <span className="sm:hidden">✓</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
              </div>
            );
          })}
        </div>
    </div>
  );
}
